import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import HomePage from "./pages/HomePage";
import Pricing from "./pages/Pricing";
import ContactUs from "./pages/ContactUs";
import BookAppointment from "./pages/BookAppointment";
import IntegrationsPage from "./pages/IntegrationsPage";
import Documentation from "./pages/Documentation";
import Releases from "./pages/Releases";
import AdminLogin from "./pages/AdminLogin";
import AdminLayout from "./components/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSubAdmins from "./pages/admin/AdminSubAdmins";
import ClientSettingsPage from "./pages/admin/ClientSettingsPage";
import AdminAgents from "./pages/admin/AdminAgents";
import AdminPhoneNumbers from "./pages/admin/AdminPhoneNumbers";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminPermissions from "./pages/admin/AdminPermissions";
import AdminLogs from "./pages/admin/AdminLogs";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminResourcesPage from "./pages/admin/AdminResourcesPage";
// Client (Sub-Admin) imports
import ClientLogin from "./pages/client/ClientLogin";
import ClientLayout from "./components/client/ClientLayout";
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientUsers from "./pages/client/ClientUsers";
import ClientAgents from "./pages/client/ClientAgents";
import ClientBilling from "./pages/client/ClientBilling";
import ClientReports from "./pages/client/ClientReports";
import AgentEditor from "./pages/client/AgentEditor";
import ClientUsagePage from "./pages/client/ClientUsagePage";
import DialerPage from "./pages/client/DialerPage";
import DialerPortal from "./pages/client/DialerPortal";
import CampaignsPage from "./pages/client/CampaignsPage";
import CreateCampaignPage from "./pages/client/CreateCampaignPage";
import CampaignDetailPage from "./pages/client/CampaignDetailPage";
import DNCListPage from "./pages/client/DNCListPage";
import AgentMonitorPage from "./pages/client/AgentMonitorPage";
import AnalyticsDashboardPage from "./pages/client/AnalyticsDashboardPage";
import VoiceAnalyticsPage from "./pages/client/VoiceAnalyticsPage";
import CampaignHealthDashboard from "./pages/client/CampaignHealthDashboard";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
// New client pages
import CloneVoicePage from "./pages/client/CloneVoicePage";
import FilesPage from "./pages/client/FilesPage";
import LeadsPage from "./pages/client/LeadsPage";
import ClientSettingsPageClient from "./pages/client/ClientSettingsPage";
import DocsPage from "./pages/client/DocsPage";
import ClientIntegrationsPage from "./pages/client/ClientIntegrationsPage";
import TelnyxSettingsPage from "./pages/client/TelnyxSettingsPage";
// End User imports
import UserLogin from "./pages/UserLogin";
import UserLoginByUsername from "./pages/UserLoginByUsername";
import SignUp from "./pages/SignUp";
import UserDashboard from "./pages/UserDashboard";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import ErrorBoundary from "./components/ErrorBoundary";

// Global error fallback component
const GlobalErrorFallback = ({ error, onRetry, onRefresh }) => (
  <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center p-6">
    <div className="bg-[#0d1117] border border-gray-800 rounded-2xl p-12 max-w-lg text-center">
      <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      
      <h1 className="text-2xl font-bold text-white mb-3">
        Something went wrong
      </h1>
      
      <p className="text-gray-400 mb-8">
        We apologize for the inconvenience. The application encountered an unexpected error.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={onRefresh}
          className="bg-teal-400 hover:bg-teal-500 text-black px-6 py-3 rounded-lg font-medium transition-colors"
        >
          Refresh Page
        </button>
        
        <a
          href="/"
          className="border border-gray-600 text-gray-300 hover:border-gray-400 hover:text-white px-6 py-3 rounded-lg font-medium transition-colors"
        >
          Go to Homepage
        </a>
      </div>
    </div>
  </div>
);

function App() {
  return (
    <div className="App">
      <ErrorBoundary 
        section="Application"
        fallbackComponent={GlobalErrorFallback}
      >
        <LanguageProvider>
          <AuthProvider>
            <BrowserRouter>
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/book-appointment" element={<BookAppointment />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/docs" element={<Documentation />} />
            <Route path="/releases" element={<Releases />} />
            
            {/* User Auth Routes */}
            <Route path="/login" element={<UserLogin />} />
            <Route path="/user-login" element={<UserLoginByUsername />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/dashboard" element={<UserDashboard />} />
            
            {/* Admin Login */}
            <Route path="/admin" element={<AdminLogin />} />
            
            {/* Protected Admin Routes */}
            <Route element={<AdminLayout />}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/sub-admins" element={<AdminSubAdmins />} />
              <Route path="/admin/clients" element={<AdminSubAdmins />} />
              <Route path="/admin/clients/:clientId/settings" element={<ClientSettingsPage />} />
              <Route path="/admin/agents" element={<AdminAgents />} />
              <Route path="/admin/phone-numbers" element={<AdminPhoneNumbers />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/permissions" element={<AdminPermissions />} />
              <Route path="/admin/logs" element={<AdminLogs />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/resources" element={<AdminResourcesPage />} />
            </Route>

            {/* Client (Sub-Admin) Login */}
            <Route path="/client" element={<ClientLogin />} />
            
            {/* Protected Client Routes */}
            <Route element={<ClientLayout />}>
              <Route path="/client/dashboard" element={<ClientDashboard />} />
              <Route path="/client/usage" element={<ClientUsagePage />} />
              <Route path="/client/users" element={<ClientUsers />} />
              <Route path="/client/agents" element={<ClientAgents />} />
              <Route path="/client/agents/:agentId" element={<AgentEditor />} />
              <Route path="/client/voice-analytics" element={<VoiceAnalyticsPage />} />
              <Route path="/client/dialer" element={<DialerPage />} />
              <Route path="/client/dialer-portal" element={<DialerPortal />} />
              <Route path="/client/campaigns" element={<CampaignsPage />} />
              <Route path="/client/campaigns/new" element={<CreateCampaignPage />} />
              <Route path="/client/campaigns/:campaignId" element={<CampaignDetailPage />} />
              <Route path="/client/campaigns/health" element={<CampaignHealthDashboard />} />
              <Route path="/client/dnc" element={<DNCListPage />} />
              <Route path="/client/agent-monitor" element={<AgentMonitorPage />} />
              <Route path="/client/analytics" element={<AnalyticsDashboardPage />} />
              <Route path="/client/billing" element={<ClientBilling />} />
              <Route path="/client/reports" element={<ClientReports />} />
              {/* New client pages */}
              <Route path="/client/clone-voice" element={<CloneVoicePage />} />
              <Route path="/client/files" element={<FilesPage />} />
              <Route path="/client/leads" element={<LeadsPage />} />
              <Route path="/client/settings" element={<ClientSettingsPageClient />} />
              <Route path="/client/telnyx-settings" element={<TelnyxSettingsPage />} />
              <Route path="/client/docs" element={<DocsPage />} />
              <Route path="/client/integrations" element={<ClientIntegrationsPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </LanguageProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
